import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
const VISIBLE_SITE_COUNT = 2;
const POPOVER_GAP = 8;
const SiteChip = ({ siteCode, url }) => {
    const className = 'inline-block rounded bg-blue-50 px-1.5 py-0.5 text-xs font-semibold text-blue-700';
    if (!url) {
        return React.createElement("span", { className: className }, siteCode);
    }
    return (React.createElement("a", { href: url, target: "_blank", rel: "noopener noreferrer", title: `View the article on ${siteCode}`, className: `${className} hover:bg-blue-100 hover:underline` }, siteCode));
};
const SitesPopover = ({ id, heading, siteCodes, buildSiteUrl, triggerRef, onClose }) => {
    const popoverRef = useRef(null);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    useLayoutEffect(() => {
        const place = () => {
            var _a;
            const trigger = (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            const popover = popoverRef.current;
            if (!trigger || !popover) {
                return;
            }
            const { offsetWidth: width, offsetHeight: height } = popover;
            const fitsBelow = trigger.bottom + POPOVER_GAP + height <= window.innerHeight;
            const top = fitsBelow
                ? trigger.bottom + POPOVER_GAP
                : Math.max(POPOVER_GAP, trigger.top - POPOVER_GAP - height);
            const left = Math.min(Math.max(POPOVER_GAP, trigger.left), window.innerWidth - width - POPOVER_GAP);
            setPosition({ top, left });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [triggerRef]);
    useEffect(() => {
        var _a;
        (_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        const closeOnOutsideClick = (event) => {
            var _a, _b;
            const target = event.target;
            if (!((_a = popoverRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) &&
                !((_b = triggerRef.current) === null || _b === void 0 ? void 0 : _b.contains(target))) {
                onClose();
            }
        };
        const closeOnEscape = (event) => {
            var _a;
            if (event.key === 'Escape') {
                onClose();
                (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            }
        };
        document.addEventListener('mousedown', closeOnOutsideClick);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutsideClick);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [onClose, triggerRef]);
    // fixed positioning lets the popover escape the table's scroll box
    return (React.createElement("div", { id: id, ref: popoverRef, role: "dialog", "aria-label": heading, tabIndex: -1, style: { top: position.top, left: position.left }, className: "fixed z-50 w-[min(27rem,calc(100vw-1rem))] rounded-xl border border-gray-200 bg-white p-4 text-left shadow-xl focus:outline-none" },
        React.createElement("div", { className: "flex items-start justify-between gap-4" },
            React.createElement("div", null,
                React.createElement("p", { className: "text-base font-semibold text-gray-900" }, heading),
                React.createElement("p", { className: "mt-0.5 text-sm text-gray-500" }, "Select a site to view the article there.")),
            React.createElement("button", { type: "button", onClick: () => {
                    var _a;
                    onClose();
                    (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                }, "aria-label": "Close", title: "Close", className: "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100" },
                React.createElement("span", { className: "material-symbols-outlined text-xl", "aria-hidden": true }, "close"))),
        React.createElement("div", { className: "mt-3 flex max-h-72 flex-wrap gap-2 overflow-y-auto" }, siteCodes.map((siteCode) => {
            return (React.createElement(SiteChip, { key: siteCode, siteCode: siteCode, url: buildSiteUrl(siteCode) }));
        }))));
};
const BlogArticleSites = ({ blogArticleId, siteCodes, isOnEverySite, buildSiteUrl }) => {
    const [isOpen, setIsOpen] = useState(false);
    const triggerRef = useRef(null);
    const popoverId = `blog-article-${blogArticleId}-sites`;
    const close = useCallback(() => {
        setIsOpen(false);
    }, []);
    if (siteCodes.length === 0) {
        return (React.createElement("span", { className: "inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800" },
            React.createElement("span", { className: "material-symbols-outlined text-base", "aria-hidden": true }, "warning"),
            "No sites"));
    }
    const chipCodes = isOnEverySite ? [] : siteCodes.slice(0, VISIBLE_SITE_COUNT);
    const hiddenCount = siteCodes.length - chipCodes.length;
    const heading = isOnEverySite
        ? `All ${siteCodes.length} sites`
        : `${siteCodes.length} sites`;
    return (React.createElement("div", { className: "flex items-center gap-1.5 whitespace-nowrap" },
        chipCodes.map((siteCode) => {
            return (React.createElement(SiteChip, { key: siteCode, siteCode: siteCode, url: buildSiteUrl(siteCode) }));
        }),
        hiddenCount > 0 && (React.createElement("button", { ref: triggerRef, type: "button", onClick: () => {
                setIsOpen(!isOpen);
            }, "aria-haspopup": "dialog", "aria-expanded": isOpen, "aria-controls": isOpen ? popoverId : undefined, className: isOnEverySite
                ? 'inline-flex items-center gap-0.5 rounded-md border border-gray-200 bg-gray-50 py-0.5 pr-1 pl-2 text-sm font-semibold text-gray-800 hover:bg-gray-100'
                : 'rounded px-1 text-xs font-semibold text-blue-700 hover:underline' },
            isOnEverySite
                ? `All sites (${siteCodes.length})`
                : `+${hiddenCount} more`,
            isOnEverySite && (React.createElement("span", { className: "material-symbols-outlined text-lg", "aria-hidden": true }, "expand_more")))),
        isOpen && (React.createElement(SitesPopover, { id: popoverId, heading: heading, siteCodes: siteCodes, buildSiteUrl: buildSiteUrl, triggerRef: triggerRef, onClose: close }))));
};
export default BlogArticleSites;
